import { useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { searchContents } from "../services/api/contents/contents.api";

const PAGE_SIZE = 100;

/**
 * Builds a debounced, cancellable `loadOptions` for react-select's AsyncSelect,
 * backed by GET /api/v2/ContentSearch.
 * @param {object} params
 * @param {string[]} params.types - ContentTypeDto values to search.
 * @param {number|string} [params.excludeId] - Content id removed from the results.
 * @param {number} [params.delay] - Debounce in milliseconds (default: 500ms).
 * @returns {(inputValue: string, callback: Function) => void}
 */
export const useContentSearchLoader = ({ types, excludeId, delay = 500 }) => {
  const timerRef = useRef(null);
  const sourceRef = useRef(null);

  const cancelPending = useCallback(() => {
    clearTimeout(timerRef.current);
    if (sourceRef.current) sourceRef.current.cancel();
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  return useCallback(
    (inputValue, callback) => {
      cancelPending();

      if (!inputValue) {
        callback([]);
        return;
      }

      timerRef.current = setTimeout(() => {
        const source = axios.CancelToken.source();
        sourceRef.current = source;

        searchContents(
          { Term: inputValue, Types: types, PageSize: PAGE_SIZE },
          { cancelToken: source.token }
        )
          .then((response) =>
            callback(
              response.items.items
                .filter((item) => item.id !== Number(excludeId))
                .map((item) => ({ ...item, value: item.id, label: item.title }))
            )
          )
          .catch((error) => {
            if (!axios.isCancel(error)) callback([]);
          });
      }, delay);
    },
    [types, excludeId, delay, cancelPending]
  );
};
